import { useQuery } from '@tanstack/react-query'
import { apiClient } from './client'
import { queryKeys } from '../lib/query'
import { useAuthStore } from '../store/authStore'
import type { Lead, Project, Activity, User } from '@1130-studio/shared'

interface DashboardStats {
  newLeads: number
  totalClients: number
  activeProjects: number
  activeBudget: string
  pendingPayments: {
    count: number
    total: string
  }
  receivedThisMonth: string
}

interface ProjectsByStage {
  name: string
  color: string
  count: number
}

interface RecentLead extends Omit<Lead, 'assignedUser'> {
  assignedUser?: Pick<User, 'id' | 'username'>
}

interface UpcomingProject extends Omit<Project, 'stage'> {
  stage?: {
    id: string
    name: string
    color: string
    isFinal: boolean
  }
}

interface RecentActivity extends Omit<Activity, 'user'> {
  user?: Pick<User, 'id' | 'username'>
}

interface DashboardData {
  stats: DashboardStats
  leadsByStatus: Record<string, number>
  projectsByStage: ProjectsByStage[]
  recentLeads: RecentLead[]
  upcomingDeadlines: UpcomingProject[]
  recentActivities: RecentActivity[]
}

export function useDashboard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.dashboard.stats,
    queryFn: () => apiClient<DashboardData>('/dashboard/stats'),
    refetchInterval: 60000, // Refetch every minute
    enabled: isAuthenticated,
  })
}

export type { DashboardData, DashboardStats, ProjectsByStage, RecentLead, UpcomingProject, RecentActivity }
